import { BaseAPI } from 'API/BaseApi';
import {
  convertFromUpdatedDocumentResponseToUpdatedLatestDocumentVersionArray,
  IStandardDocumentVersionData,
  IUpdatedDocumentResponse,
} from 'types/order/documents/shared/interfaces';
import { eAPITags } from '../../eAPITags';
import { ICheckMergeType } from 'types/order/documents/ICheckMergeType';
import { UUID } from '../../../types/shared/UUID';
import { IDocumentVersionResponse } from '../../../types/order/documents/shared/IDocumentVersionResponse';

interface DeleteDocumentParams {
  orderId: UUID;
  documentId: UUID;
  versionId: UUID;
}

export const DocumentApi = BaseAPI.injectEndpoints({
  endpoints: (build) => ({
    listAdminStandardDocuments: build.query<
      IStandardDocumentVersionData[],
      {
        orderId: string;
        categories?: string[];
      }
    >({
      query: ({ orderId, categories }) => {
        return {
          url: `/timber/api/internal/orders/${orderId}/documents/all`,
          params: {
            categories: categories,
          },
        };
      },
      transformResponse: (response: IUpdatedDocumentResponse[], meta, arg) => {
        return convertFromUpdatedDocumentResponseToUpdatedLatestDocumentVersionArray(response);
      },
      providesTags: (response, error, { orderId, categories }) => [
        { type: eAPITags.SectionData, id: orderId },
        { type: eAPITags.AdminDocuments, id: orderId },
      ],
    }),
    mergeChecks: build.mutation<IStandardDocumentVersionData, ICheckMergeType>({
      query: ({ orderId, documentIds, label, outputCategory }) => ({
        url: `/timber/api/internal/checks/merge`,
        method: 'POST',
        body: {
          orderId,
          documentIds,
          label,
          outputCategory,
        },
      }),
    }),
    deleteDocument: build.mutation<void, DeleteDocumentParams>({
      query: ({ documentId, versionId }) => ({
        url: `/timber/api/internal/documents/${documentId}/versions/${versionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (response, error, { orderId }) => [{ type: eAPITags.SectionData, id: orderId }],
    }),
    generateDocument: build.mutation<IDocumentVersionResponse, { data: { orderId: string; category: string } }>({
      query: ({ data }) => ({
        url: `/timber/api/internal/documents`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: IDocumentVersionResponse) => response,
      invalidatesTags: (result, error, { data }) => [{ type: eAPITags.AdminDocuments, id: data.orderId }],
    }),
  }),
});

export const {
  useListAdminStandardDocumentsQuery,
  useMergeChecksMutation,
  useDeleteDocumentMutation,
  useGenerateDocumentMutation,
} = DocumentApi;
